import i18next from 'i18next';
import { ApiClientException } from './apiClientException';

/** Page contact studio (déjà utilisée dans AlmaPaymentOverlay). */
export const SUPPORT_CONTACT_PATH = '/contact/';

export type UserErrorContext =
  | 'checkout'
  | 'contract'
  | 'booking'
  | 'cancelBooking'
  | 'cancelWaitlist'
  | 'loadClasses'
  | 'auth'
  | 'sca'
  | 'refund'
  | 'paymentMethod'
  | 'passwordChange';

export interface UserFacingError {
  title: string;
  description?: string;
}

function translate(key: string): string | undefined {
  return i18next.exists(key) ? i18next.t(key) : undefined;
}

function isLikelyTechnicalMessage(message: string): boolean {
  const technical =
    /Code:|Level:\s*Error|TransactionIDs|JSON\.parse|Status:\s*\w+|failed validation|BLL\./i;
  return technical.test(message) || message.length > 120;
}

/**
 * Messages orientés utilisateur (inspirés des bonnes pratiques Wix UX) :
 * expliquer ce qui s'est passé, rassurer, indiquer quoi faire, proposer une issue.
 */
export function resolveUserError(context: UserErrorContext, error?: unknown): UserFacingError {
  if (error instanceof ApiClientException) {
    const title = error.getMessage();
    const description = translate(`errors.context.${context}.apiDescription`);
    return description ? { title, description } : { title };
  }

  if (error instanceof Error) {
    if (isNetworkError(error)) {
      return {
        title: translate('errors.fallback.network.title') ?? 'Connexion interrompue',
        description:
          translate('errors.fallback.network.description') ??
          'Nous n’avons pas pu joindre nos serveurs. Vérifiez votre connexion internet, puis réessayez.',
      };
    }

    if (context === 'sca' && /3D Secure|Authentification/i.test(error.message)) {
      return {
        title:
          translate('errors.context.sca.failed.title') ??
          'Authentification bancaire non validée',
        description:
          translate('errors.context.sca.failed.description') ??
          'Votre banque n’a pas confirmé le paiement. Aucun abonnement ni achat n’a été activé. Réessayez ou utilisez une autre carte.',
      };
    }

    if (!isLikelyTechnicalMessage(error.message)) {
      return { title: error.message };
    }
  }

  const title =
    translate(`errors.context.${context}.title`) ?? defaultTitle(context);
  const description =
    translate(`errors.context.${context}.description`) ?? defaultDescription(context);

  return { title, description };
}

function isNetworkError(error: Error): boolean {
  return /timeout|network|Failed to fetch|Load failed|abort/i.test(error.message);
}

function defaultTitle(context: UserErrorContext): string {
  switch (context) {
    case 'checkout':
      return 'Le paiement n’a pas abouti';
    case 'contract':
      return 'L’abonnement n’a pas pu être activé';
    case 'booking':
      return 'La réservation n’a pas été enregistrée';
    case 'cancelBooking':
      return 'L’annulation n’a pas été prise en compte';
    case 'cancelWaitlist':
      return 'L’annulation n’a pas abouti';
    case 'loadClasses':
      return 'Le planning n’a pas pu s’afficher';
    case 'auth':
      return 'La connexion n’a pas abouti';
    case 'sca':
      return 'Authentification bancaire non validée';
    case 'refund':
      return 'Le remboursement automatique n’a pas abouti';
    case 'paymentMethod':
      return 'Votre moyen de paiement n’a pas pu être enregistré';
    case 'passwordChange':
      return 'Le mot de passe n’a pas pu être modifié';
  }
}

function defaultDescription(context: UserErrorContext): string {
  const contact = `contactez-nous via ${SUPPORT_CONTACT_PATH}`;

  switch (context) {
    case 'checkout':
      return `Un problème technique nous a empêchés de finaliser votre achat. Aucun débit n’a été confirmé sur votre carte. Réessayez dans quelques instants — si le souci persiste, ${contact}.`;
    case 'contract':
      return `Nous n’avons pas pu activer votre abonnement. Aucun prélèvement récurrent n’a été mis en place. Réessayez, ou ${contact} si le souci persiste.`;
    case 'booking':
      return `Votre place n’a pas été réservée et vos crédits n’ont pas été débités. Réessayez, choisissez une autre séance, ou ${contact} si le souci persiste.`;
    case 'cancelBooking':
      return `Votre réservation reste active et vos crédits ne sont pas affectés. Réessayez dans quelques instants, ou ${contact} si le souci persiste.`;
    case 'cancelWaitlist':
      return `Vous restez inscrit·e sur la liste d’attente. Réessayez dans quelques instants, ou ${contact} si le souci persiste.`;
    case 'loadClasses':
      return `Un problème temporaire nous empêche d’afficher les séances. Vos réservations et vos crédits ne sont pas affectés. Actualisez la page ou réessayez plus tard.`;
    case 'auth':
      return `Nous n’avons pas pu finaliser votre connexion. La fenêtre d’authentification a peut-être été fermée trop tôt. Réessayez ou utilisez une autre méthode de connexion.`;
    case 'sca':
      return `Votre banque n’a pas confirmé le paiement. Aucun achat ni abonnement n’a été activé. Réessayez ou utilisez une autre carte.`;
    case 'refund':
      return `Votre paiement a peut-être été débité, mais nous n’avons pas pu créditer votre compte. Contactez-nous via ${SUPPORT_CONTACT_PATH} en précisant l’heure exacte du paiement — nous régularisons rapidement.`;
    case 'paymentMethod':
      return `Vos coordonnées bancaires n’ont pas été enregistrées. Aucun débit n’a eu lieu. Vérifiez les informations saisies puis réessayez, ou ${contact} si le souci persiste.`;
    case 'passwordChange':
      return `Vos identifiants n’ont pas été mis à jour. Réessayez dans quelques instants, ou ${contact} si le souci persiste.`;
  }
}

/** Texte court pour overlays (Alma, contrat) sans description longue. */
export function resolveOverlayError(
  step: 'verifying' | 'processing' | 'crediting' | 'unknown',
  rawMessage?: string
): string {
  if (rawMessage?.includes('Mindbody crediting failed') || step === 'crediting') {
    return (
      translate('errors.context.alma.creditingFailed') ??
      'Votre paiement a été accepté, mais nous n’avons pas pu créditer votre compte. Un remboursement automatique est en cours.'
    );
  }
  if (rawMessage?.includes('Payment not validated') || step === 'verifying' || step === 'processing') {
    return (
      translate('errors.context.alma.validationFailed') ??
      'Nous n’avons pas pu confirmer votre paiement avec Alma. Aucun crédit n’a été ajouté.'
    );
  }
  return (
    translate('errors.context.alma.genericFailed') ??
    'Nous n’avons pas pu finaliser votre paiement après plusieurs tentatives.'
  );
}
